/**
 * Legacy Item Detail Redirect
 * Story PU-7.15: Update Navigation to Plugin Item Detail Routes
 *
 * Redirects legacy /calendar/item/:id/draft routes to the new
 * plugin item detail page at /plugins/marketing/item/:id.
 *
 * Preserves tab query parameter during redirect for deep linking support.
 * Uses Navigate with replace for clean browser history.
 *
 * Redirect Mapping:
 * - /calendar/item/:id/draft → /plugins/marketing/item/:id?tab=content
 * - /calendar/item/:id/draft?tab=visual → /plugins/marketing/item/:id?tab=content
 *   (T-20260913-4 retired the `visual` tab; old links land on the draft instead
 *   of a tab that no longer exists)
 * - /calendar/item/:id/draft?tab=video → /plugins/marketing/item/:id?tab=video
 * - /calendar/item/:id/draft?tab=email → /plugins/marketing/item/:id?tab=email
 *
 * @see docs/architecture-plugin-item-detail-view.md#Navigation Updates
 */

import { Navigate, useParams, useSearchParams } from 'react-router-dom';

/**
 * Redirects /calendar/item/:id/draft to /plugins/marketing/item/:id
 *
 * AC-5: Legacy URL redirects to new plugin URL preserving tab parameter
 * AC-6: Tab parameter preserved during redirect
 *
 * @example
 * // Route: /calendar/item/123/draft
 * // Redirects to: /plugins/marketing/item/123?tab=content
 *
 * @example
 * // Route: /calendar/item/123/draft?tab=video
 * // Redirects to: /plugins/marketing/item/123?tab=video
 */
export function LegacyItemDetailRedirect() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();

  // DELIBERATELY hard-codes a tab, unlike `PluginCalendarPage.buildItemHref`,
  // which stopped sending one in T-20260918-1. The two are not the same case:
  // here the tab IS the user's expressed intent, carried over from the legacy
  // bookmark they clicked, so dropping it would discard information the URL
  // actually contains. A calendar click carries no such intent. This route is
  // also marketing-and-calendar-only, so it needs no `?dataType=` — the
  // server's pre-4.4 default reads the right substrate for exactly these rows;
  // a generic caller reaching a non-calendar `plugin_items` row is the case
  // that requires it. See the comment above `buildItemHref` for that rule.
  //
  // Get tab from query params, default to 'content' if not specified. The
  // retired `visual` tab (T-20260913-4) maps to `content`.
  const requested = searchParams.get('tab') || 'content';
  const tab = requested === 'visual' ? 'content' : requested;

  // Build target path with preserved tab parameter
  const targetPath = `/plugins/marketing/item/${id}?tab=${tab}`;

  return <Navigate to={targetPath} replace />;
}

export default LegacyItemDetailRedirect;
